<template>
	<section
		class="page page-content"
		v-bind:class="{
			'has-scrolled': hasScrolled,
		}"
	>
		<ChalkyUiPageHero
			description="Articles, tutorials, and videos from the billiards community."
			title="Content"
			v-bind:imageUrl="$asset('image/fpo/pagehero-01.jpg')"
		/>

		<SceneContent
			ref="sceneContent"
			v-bind:newsCollection="newsCollection"
			v-bind:tutorialCollection="tutorialCollection"
			v-bind:videoCollection="videoCollection"
			v-on:content:select="Handle_OnSelectContent"
		/>
	</section>
</template>

<script lang="ts">
	import * as Mixins from '@/Mixins';
	import ChalkySticks from '@chalkysticks/sdk';
	import PageBase from '@/Core/Page.vue';
	import { Component, Prop } from 'vue-property-decorator';

	/**
	 * Content index page controller. Owns data collections for news,
	 * videos, and tutorials. Delegates tabbed presentation to
	 * SceneContent and handles navigation to content detail pages.
	 *
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package Page
	 * @project ChalkySticks App
	 */
	@Component({
		mixins: [Mixins.AutoScroll, Mixins.StickyScroll],
	})
	export default class PageContent extends PageBase {
		/**
		 * Collection of news content
		 *
		 * @type ChalkySticks.Collection.Content
		 */
		@Prop({
			default: () => ChalkySticks.Factory.Content.collection({ limit: 12 }).news(),
		})
		public newsCollection!: ChalkySticks.Collection.Content;

		/**
		 * Collection of tutorial content
		 *
		 * @type ChalkySticks.Collection.Content
		 */
		@Prop({
			default: () => ChalkySticks.Factory.Content.collection({ limit: 12 }).tutorials(),
		})
		public tutorialCollection!: ChalkySticks.Collection.Content;

		/**
		 * Collection of video content
		 *
		 * @type ChalkySticks.Collection.Content
		 */
		@Prop({
			default: () => ChalkySticks.Factory.Content.collection({ limit: 12 }).tags(['youtube']),
		})
		public videoCollection!: ChalkySticks.Collection.Content;

		/**
		 * Configuration for sticky scroll behavior
		 *
		 * @type Record<string, any>
		 */
		protected stickyConfig: any = {
			scrollThreshold: 100,
		};

		/**
		 * Fetches next page of results via the Scene.
		 *
		 * @return Promise<void>
		 */
		protected async triggerLoadMore(): Promise<void> {
			await (this.$refs.sceneContent as any).loadMore();
		}

		// region: Event Handlers
		// ---------------------------------------------------------------------------

		/**
		 * Handle content item selection. Navigate to the detail page.
		 *
		 * @param ChalkySticks.Model.Content contentModel
		 * @return Promise<void>
		 */
		protected async Handle_OnSelectContent(contentModel: ChalkySticks.Model.Content): Promise<void> {
			this.$router.push({
				name: 'PageContentItem',
				params: {
					id: contentModel.getSlug(),
				},
			});
		}

		// endregion: Event Handlers
	}
</script>

<style lang="scss">
	// Layout
	// ---------------------------------------------------------------------------

	.page-content {
		background-color: var(--chalky-blue-4);
	}
</style>
